import { useState, useEffect } from "react";
import Layout from "../components/Layout.jsx";
import { getAccount, masterSupabase } from "../lib/supabase.js";
import { useUserRole, hasPermission } from "../App.jsx";

const platformUrl = import.meta.env.VITE_SUPABASE_URL;

function useFonts() {
  useEffect(() => {
    [
      { id: "gfont-instrument-serif", href: "https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&display=swap" },
      { id: "gfont-dm-sans", href: "https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;0,9..40,600;1,9..40,400&display=swap" },
    ].forEach((f) => {
      if (!document.getElementById(f.id)) {
        const link = document.createElement("link");
        link.id = f.id; link.rel = "stylesheet"; link.href = f.href;
        document.head.appendChild(link);
      }
    });
  }, []);
}

const L = {
  bg: "#fafaf8", text: "#1a1a1a", textSoft: "#444444", muted: "#8a8a8a",
  dim: "#c0bdb8", border: "#e5e3de", borderLight: "#f0eee9",
  surface: "#ffffff", inputBg: "#f6f5f2",
};
const sans = "'DM Sans', -apple-system, sans-serif";
const serif = "'Instrument Serif', Georgia, serif";

const iS = { padding: "9px 14px", background: L.inputBg, border: `1px solid ${L.border}`, borderRadius: 8, color: L.text, fontSize: 13, outline: "none", fontFamily: sans, boxSizing: "border-box" };
const lS = { display: "block", fontSize: 10, color: L.muted, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 5, fontWeight: 600 };
const btnGhost = { padding: "6px 12px", background: "none", border: `1px solid ${L.border}`, borderRadius: 6, fontSize: 11, fontWeight: 500, cursor: "pointer", fontFamily: sans, color: L.muted, transition: "color 0.15s" };

const ROLES = [
  { key: "owner", label: "Owner", desc: "Full access, billing, can manage team" },
  { key: "admin", label: "Admin", desc: "Full access, can manage team" },
  { key: "editor", label: "Editor", desc: "Can edit products, locations, documents" },
  { key: "sales", label: "Sales", desc: "Training, analytics, conversations & leads — nothing else" },
  { key: "viewer", label: "Viewer", desc: "Read-only access to dashboard" },
];

export default function Team() {
  useFonts();
  const account = getAccount();
  const { role: currentUserRole, userId } = useUserRole();
  const canManage = hasPermission(currentUserRole, "manageTeam");
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviting, setInviting] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteName, setInviteName] = useState("");
  const [inviteRole, setInviteRole] = useState("admin");
  const [message, setMessage] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState(null);
  const [resetSending, setResetSending] = useState(null);

  const loadMembers = async () => {
    if (!account?.id) return;
    const { data } = await masterSupabase.from("account_users").select("*").eq("account_id", account.id).order("invited_at");
    setMembers(data || []);
    setLoading(false);
  };

  useEffect(() => { loadMembers(); }, [account?.id]);

  const flash = (type, text) => { setMessage({ type, text }); setTimeout(() => setMessage(null), 5000); };

  const handleInvite = async () => {
    if (!inviteEmail.trim() || !canManage) return;
    setInviting(true);
    try {
      const res = await fetch(`${platformUrl}/functions/v1/invite-user`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ account_id: account.id, email: inviteEmail.trim(), name: inviteName.trim() || null, role: inviteRole }) });
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      flash("success", data.message);
      setInviteEmail(""); setInviteName(""); setShowInvite(false);
      loadMembers();
    } catch (err) { flash("error", err.message); }
    setInviting(false);
  };

  const handleDeactivate = async (member) => {
    if (!canManage || !confirm(`Remove ${member.email} from this account?`)) return;
    await masterSupabase.from("account_users").update({ is_active: false }).eq("id", member.id);
    flash("success", `${member.email} removed.`);
    loadMembers();
  };

  const handleChangeRole = async (member, newRole) => {
    if (!canManage) return;
    if (newRole === "owner" && currentUserRole !== "owner") { flash("error", "Only an owner can assign the owner role."); return; }
    if (member.role === "owner" && newRole !== "owner") {
      if (members.filter(m => m.role === "owner" && m.is_active).length <= 1) { flash("error", "Cannot remove the last owner."); return; }
    }
    await masterSupabase.from("account_users").update({ role: newRole }).eq("id", member.id);
    flash("success", `${member.email} is now ${newRole}.`);
    loadMembers();
  };

  const handleSendReset = async (member) => {
    if (!canManage && member.auth_user_id !== userId) return;
    setResetSending(member.email);
    try {
      const { error } = await masterSupabase.auth.resetPasswordForEmail(member.email, { redirectTo: window.location.origin + "/" });
      if (error) throw error;
      flash("success", `Password reset email sent to ${member.email}.`);
    } catch (err) { flash("error", `Failed to send reset: ${err.message}`); }
    setResetSending(null);
  };

  const handleChangePassword = async () => {
    setPasswordMessage(null);
    if (newPassword.length < 8) { setPasswordMessage({ type: "error", text: "Password must be at least 8 characters." }); return; }
    if (newPassword !== confirmPassword) { setPasswordMessage({ type: "error", text: "Passwords don't match." }); return; }
    setPasswordSaving(true);
    const { error } = await masterSupabase.auth.updateUser({ password: newPassword });
    if (error) setPasswordMessage({ type: "error", text: error.message });
    else { setPasswordMessage({ type: "success", text: "Password updated." }); setNewPassword(""); setConfirmPassword(""); }
    setPasswordSaving(false);
  };

  const activeMembers = members.filter(m => m.is_active);
  const inactiveMembers = members.filter(m => !m.is_active);

  return (
    <Layout>
      <style>{`@keyframes ov-up { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }`}</style>

      <div style={{ minHeight: "calc(100vh - 56px)", background: L.bg, margin: -28, padding: "40px 44px", fontFamily: sans, color: L.text }}>
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 28, animation: "ov-up 0.5s ease both" }}>
          <div>
            <h1 style={{ fontSize: 36, fontWeight: 400, lineHeight: 1.1, margin: "0 0 8px", fontFamily: serif, fontStyle: "italic" }}>Team</h1>
            <p style={{ margin: 0, fontSize: 13, color: L.muted }}>Manage who has access to this dashboard</p>
          </div>
          {canManage && (
            <button onClick={() => setShowInvite(!showInvite)} style={{
              padding: "9px 20px", background: L.text, border: "none", borderRadius: 8,
              color: L.bg, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: sans,
            }}>
              {showInvite ? "Cancel" : "+ Invite member"}
            </button>
          )}
        </div>

        {/* Permission notice */}
        {!canManage && (
          <div style={{ padding: "10px 16px", borderRadius: 8, marginBottom: 18, fontSize: 12, background: "#eff6ff", color: "#1d4ed8", border: "1px solid #bfdbfe", animation: "ov-up 0.3s ease both" }}>
            You have <strong>{currentUserRole}</strong> access. Contact an admin or owner to manage team members.
          </div>
        )}

        {/* Flash message */}
        {message && (
          <div style={{
            padding: "10px 16px", borderRadius: 8, marginBottom: 18, fontSize: 12,
            background: message.type === "error" ? "#fef2f2" : "#f0fdf4",
            color: message.type === "error" ? "#dc2626" : "#15803d",
            border: `1px solid ${message.type === "error" ? "#fecaca" : "#bbf7d0"}`,
            animation: "ov-up 0.3s ease both",
          }}>
            {message.text}
          </div>
        )}

        {/* Invite form */}
        {showInvite && canManage && (
          <div style={{ background: L.surface, border: `1px solid ${L.border}`, borderRadius: 12, padding: 24, marginBottom: 22, animation: "ov-up 0.3s ease both" }}>
            <div style={{ fontSize: 11, color: L.muted, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 16 }}>Invite a team member</div>
            <div style={{ display: "flex", gap: 14, alignItems: "flex-end", flexWrap: "wrap" }}>
              <div style={{ flex: 2, minWidth: 200 }}>
                <label style={lS}>Email *</label>
                <input type="email" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} placeholder="colleague@company.com" style={{ ...iS, width: "100%" }} />
              </div>
              <div style={{ flex: 1, minWidth: 140 }}>
                <label style={lS}>Name</label>
                <input value={inviteName} onChange={(e) => setInviteName(e.target.value)} placeholder="Optional" style={{ ...iS, width: "100%" }} />
              </div>
              <div style={{ flex: 1, minWidth: 120 }}>
                <label style={lS}>Role</label>
                <select value={inviteRole} onChange={(e) => setInviteRole(e.target.value)} style={{ ...iS, width: "100%", cursor: "pointer" }}>
                  {ROLES.filter(r => currentUserRole === "owner" || r.key !== "owner").map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
                </select>
              </div>
              <button onClick={handleInvite} disabled={inviting || !inviteEmail.trim()} style={{
                padding: "9px 24px", background: L.text, border: "none", borderRadius: 8,
                color: L.bg, fontSize: 13, fontWeight: 600, fontFamily: sans,
                cursor: inviting ? "not-allowed" : "pointer", opacity: inviting ? 0.5 : 1,
              }}>
                {inviting ? "Sending..." : "Send invite"}
              </button>
            </div>
            <p style={{ fontSize: 12, color: L.muted, marginTop: 12, marginBottom: 0, lineHeight: 1.5 }}>They'll receive an email with a link to set their password and access the dashboard.</p>
          </div>
        )}

        {/* Roles legend */}
        <div style={{ display: "flex", gap: 10, marginBottom: 18, flexWrap: "wrap", animation: "ov-up 0.5s ease 0.05s both" }}>
          {ROLES.map((r) => (
            <div key={r.key} style={{ padding: "6px 14px", background: L.surface, border: `1px solid ${L.border}`, borderRadius: 8, fontSize: 11 }}>
              <span style={{ fontWeight: 600 }}>{r.label}</span> <span style={{ color: L.muted }}>— {r.desc}</span>
            </div>
          ))}
        </div>

        {/* Members list */}
        <div style={{ background: L.surface, border: `1px solid ${L.border}`, borderRadius: 12, overflow: "hidden", animation: "ov-up 0.5s ease 0.08s both" }}>
          <div style={{ padding: "12px 22px", borderBottom: `1px solid ${L.border}`, display: "flex", justifyContent: "space-between", fontSize: 11, color: L.muted, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.1em" }}>
            <span>Team members</span>
            <span>{activeMembers.length} active</span>
          </div>

          {loading ? (
            <div style={{ padding: 40, textAlign: "center", color: L.muted, fontSize: 13 }}>Loading...</div>
          ) : members.length === 0 ? (
            <div style={{ padding: 48, textAlign: "center", color: L.muted, fontSize: 13, lineHeight: 1.6 }}>
              No team members yet. {canManage ? 'Click "Invite member" to add your first.' : "Ask an admin to invite team members."}
            </div>
          ) : activeMembers.map((m) => {
            const isCurrentUser = m.auth_user_id === userId;
            const initials = (m.name || m.email)?.[0]?.toUpperCase() || "?";
            return (
              <div key={m.id} style={{ padding: "16px 22px", borderBottom: `1px solid ${L.borderLight}`, display: "flex", alignItems: "center", gap: 14 }}>
                <div style={{
                  width: 38, height: 38, borderRadius: "50%", background: L.inputBg,
                  border: `1px solid ${L.border}`, display: "flex", alignItems: "center",
                  justifyContent: "center", fontSize: 14, fontWeight: 600, color: L.textSoft, flexShrink: 0,
                }}>
                  {initials}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 500 }}>
                    {m.name || m.email}
                    {isCurrentUser && <span style={{ fontSize: 11, color: L.dim, marginLeft: 8 }}>(you)</span>}
                  </div>
                  <div style={{ fontSize: 12, color: L.muted }}>{m.email}</div>
                  <div style={{ display: "flex", gap: 8, marginTop: 3, fontSize: 11, color: L.dim }}>
                    {m.accepted_at ? <span>Joined {new Date(m.accepted_at).toLocaleDateString()}</span> : <span style={{ color: "#b45309" }}>Invite pending</span>}
                    {m.last_login_at && <span>· Last login {new Date(m.last_login_at).toLocaleDateString()}</span>}
                  </div>
                </div>
                {canManage ? (
                  <select value={m.role} onChange={(e) => handleChangeRole(m, e.target.value)} style={{
                    ...iS, padding: "5px 10px", fontSize: 11, fontWeight: 600,
                    textTransform: "uppercase", cursor: "pointer", width: "auto",
                  }}>
                    {ROLES.filter(r => currentUserRole === "owner" || r.key !== "owner").map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
                  </select>
                ) : (
                  <span style={{ padding: "5px 12px", background: L.inputBg, border: `1px solid ${L.border}`, borderRadius: 8, fontSize: 11, fontWeight: 600, textTransform: "uppercase", color: L.textSoft }}>{m.role}</span>
                )}
                {(canManage || isCurrentUser) && m.accepted_at && (
                  <button onClick={() => handleSendReset(m)} disabled={resetSending === m.email} style={btnGhost}>
                    {resetSending === m.email ? "Sending..." : "Reset PW"}
                  </button>
                )}
                {canManage && m.role !== "owner" && !isCurrentUser && (
                  <button onClick={() => handleDeactivate(m)} style={{ ...btnGhost, color: "#dc2626", borderColor: "#fecaca" }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = "#fef2f2"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = "none"; }}
                  >
                    Remove
                  </button>
                )}
              </div>
            );
          })}

          {/* Inactive members */}
          {inactiveMembers.length > 0 && (<>
            <div style={{ padding: "8px 22px", background: L.inputBg, fontSize: 10, color: L.muted, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.1em" }}>
              Inactive ({inactiveMembers.length})
            </div>
            {inactiveMembers.map((m) => (
              <div key={m.id} style={{ padding: "12px 22px", borderBottom: `1px solid ${L.borderLight}`, display: "flex", alignItems: "center", gap: 14, opacity: 0.45 }}>
                <div style={{ width: 38, height: 38, borderRadius: "50%", background: L.borderLight, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, color: L.dim, flexShrink: 0 }}>
                  {(m.name || m.email)?.[0]?.toUpperCase()}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13 }}>{m.name || m.email}</div>
                  <div style={{ fontSize: 11, color: L.muted }}>{m.email}</div>
                </div>
                <span style={{ fontSize: 11, color: L.dim }}>Removed</span>
              </div>
            ))}
          </>)}
        </div>

        {/* Change password */}
        <div style={{ background: L.surface, border: `1px solid ${L.border}`, borderRadius: 12, padding: 24, marginTop: 22, animation: "ov-up 0.5s ease 0.1s both" }}>
          <div style={{ fontSize: 11, color: L.muted, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 18 }}>Change my password</div>
          <div style={{ display: "flex", gap: 14, alignItems: "flex-end", flexWrap: "wrap" }}>
            <div style={{ flex: 1, minWidth: 200 }}>
              <label style={lS}>New password</label>
              <input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="Min 8 characters" style={{ ...iS, width: "100%" }} />
            </div>
            <div style={{ flex: 1, minWidth: 200 }}>
              <label style={lS}>Confirm password</label>
              <input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder="Re-enter password" style={{ ...iS, width: "100%" }} />
            </div>
            <button onClick={handleChangePassword} disabled={passwordSaving || !newPassword} style={{
              padding: "9px 22px", background: L.text, border: "none", borderRadius: 8,
              color: L.bg, fontSize: 13, fontWeight: 600, fontFamily: sans,
              cursor: (passwordSaving || !newPassword) ? "not-allowed" : "pointer",
              opacity: (passwordSaving || !newPassword) ? 0.3 : 1,
            }}>
              {passwordSaving ? "Updating..." : "Update password"}
            </button>
          </div>
          {passwordMessage && (
            <div style={{
              padding: "10px 14px", borderRadius: 8, marginTop: 14, fontSize: 12,
              background: passwordMessage.type === "error" ? "#fef2f2" : "#f0fdf4",
              color: passwordMessage.type === "error" ? "#dc2626" : "#15803d",
              border: `1px solid ${passwordMessage.type === "error" ? "#fecaca" : "#bbf7d0"}`,
            }}>
              {passwordMessage.text}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
